import { getNodesBounds, getViewportForBounds } from '@xyflow/react';
import { toPng } from 'html-to-image';
// Layout constants - matching backend spacing
const nodeWidth = 180;
const nodeHeight = 100;
const nodeSpacing = { x: 30, y: 20 };

export const getEntityColor = (index) => {
  const colors = ['#DACEE0', '#ECE4B5', '#C1D1F0', '#E8D3C6', '#D4E6F1'];
  return colors[index % colors.length];
};

export const getInitialGraphData = (entities, relationships) => {
  // implementation from original
  const nodes = entities.map((entity, index) => ({
    id: entity.id + '',
    type: 'custom-entity-node',
    position: { x: (index % 3) * 280, y: Math.floor(index / 3) * 160 },
    data: {
      name: entity.name,
      type: entity.type || 'Entity',
      isDefault: entity.isDefault,
      color: entity.colorCode || getEntityColor(index),
      entity,
      label: entity.name,
      itemCount: (entity.assets?.length || 0) + (entity.liabilities?.length || 0)
    },
    dragHandle: '.drag-handle'
  }));

  const edges = relationships.map((rel) => ({
    id: rel.id,
    source: rel.ownerId + '',
    target: rel.ownedId + '',
    type: 'custom-entity-edge',
    data: { ownership: rel.percentage, label: `${rel.percentage}%` },
    markerEnd: {
      type: 'MarkerType.ArrowClosed',
      width: 15,
      height: 15,
      color: '#ACB1BB'
    }
  }));

  return { nodes, edges }; // Add proper layout if needed
};

export const downloadImage = async (nodes) => {
  const imageWidth = 1000;
  const imageHeight = 1000;
  try {
    const nodesBounds = getNodesBounds(nodes);
    nodesBounds.width += nodeWidth * 2;
    nodesBounds.height += nodeHeight * 2;
    nodesBounds.x -= nodeWidth;
    nodesBounds.y -= nodeHeight;
    const viewportConfig = getViewportForBounds(nodesBounds, imageWidth, imageHeight, 0.5, 2);

    const viewportElement = document.querySelector('.react-flow__viewport');

    if (!viewportElement) {
      console.error('React Flow viewport not found');
      return;
    }

    const dataUrl = await toPng(viewportElement, {
      backgroundColor: '#ffffff',
      width: imageWidth,
      height: imageHeight,
      style: {
        width: imageWidth,
        height: imageHeight,
        transform: `translate(${viewportConfig.x}px, ${viewportConfig.y}px) scale(${viewportConfig.zoom})`,
      },
    });

    const link = document.createElement('a');
    link.download = `entities-graph-${new Date().toISOString().split('T')[0]}.png`;
    link.href = dataUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error('Error downloading image:', error);
  }
};

/**
 * Build an adjacency map from the edges list.
 * @param {Array} edges - Array of edge objects {sourceId, targetId}
 * @returns {Map<string, string[]>} - Adjacency list
 */
const buildAdjacencyMap = (edges) => {
  const map = new Map();

  for (const edge of edges) {
    const { source, target } = edge;
    if (!map.has(source)) {
      map.set(source, []);
    }
    map.get(source).push(target);
  }

  return map;
}

/**
 * Check if adding an edge from sourceId to targetId would create a cycle.
 * @param {string} source - The proposed source node
 * @param {string} target - The proposed target node
 * @param {Array} edges - Current array of edges
 * @returns {boolean} - true if the connection is valid (no cycle), false otherwise
 */
export const isEdgeValid = (source, target, edges) => {
  // Check if same edge already exists
  const edgeExists = edges.some(edge => 
    edge.source === source && edge.target === target
  );
  if (edgeExists) return false;

  // Build adjacency map for traversal
  const adjacency = buildAdjacencyMap(edges);
  const visited = new Set();

  // Check if target has path to source (would create family connection)
  function hasPathTo(currentId, targetId) {
    if (currentId === targetId) return true;
    if (visited.has(currentId)) return false;

    visited.add(currentId);
    const neighbors = adjacency.get(currentId) || [];

    for (const neighbor of neighbors) {
      if (hasPathTo(neighbor, targetId)) return true; 
    }

    return false;
  }

  // If target has path to source, connection would create invalid family relation
  return !hasPathTo(target, source);
}

export const getNewPositionForNewNode = (nodes) => {
  if (!nodes || nodes.length === 0) {
    return { x: 0, y: 0 };
  }

  // Get screen width for responsive calculations
  const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const containerPadding = 40; // 20px padding on each side
  const availableWidth = screenWidth - containerPadding;
  
  // Calculate how many nodes can fit in one row
  const maxNodesPerRow = Math.floor(availableWidth / (nodeWidth + nodeSpacing.x));
  
  // Ensure at least 1 node per row, but cap at 6 for very wide screens
  const nodesPerRow = Math.max(1, Math.min(maxNodesPerRow, 6));
  
  // For mobile, use fewer columns
  const isMobile = screenWidth <= 768;
  const mobileNodesPerRow = Math.max(1, Math.min(2, maxNodesPerRow));
  const finalNodesPerRow = isMobile ? mobileNodesPerRow : nodesPerRow;

  // For very small screens, ensure we don't exceed available width
  let adjustedNodesPerRow = finalNodesPerRow;
  if (screenWidth < 480) {
    adjustedNodesPerRow = Math.max(1, Math.min(1, maxNodesPerRow));
  }

  // Create a set of occupied positions for quick lookup
  const occupiedPositions = new Set();
  for (const entity of nodes) {
    const pos = entity.position || { x: 0, y: 0 };
    // Round positions to grid to handle slight variations
    const gridX = Math.round(pos.x / (nodeWidth + nodeSpacing.x));
    const gridY = Math.round(pos.y / (nodeHeight + nodeSpacing.y));
    occupiedPositions.add(`${gridX},${gridY}`);
  }

  // Find the first available position in the grid
  let row = 0;
  let col = 0;
  let found = false;

  // Search for an empty position, starting from the top-left
  while (!found) {
    const gridKey = `${col},${row}`;
    
    if (!occupiedPositions.has(gridKey)) {
      found = true;
    } else {
      // Move to next position
      col++;
      if (col >= adjustedNodesPerRow) {
        col = 0;
        row++;
      }
    }
  }

  const newX = col * (nodeWidth + nodeSpacing.x);
  const newY = row * (nodeHeight + nodeSpacing.y);

  // Add some margin to ensure the new entity is clearly visible
  const margin = 20;
  const finalX = Math.max(margin, newX);
  const finalY = Math.max(margin, newY);

  return { x: finalX, y: finalY };
}